'use client'

import { type IClause, type ILaw } from '@/lib/ris-types'
import { Row } from '@christiankaindl/lyts'
import * as styles from './Title.css'
import { Overlay, OverlayContent } from '@/components/Overlay'
import { ClipboardCheck } from 'lucide-react'
import Button from '@/components/Button/Button'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { domain, formatDate, formatNumber, getUrl, urlify } from '@/lib/utils'
import ListBox from '@/components/ListBox/ListBox'
import ListBoxRow from '@/components/ListBox/ListBoxRow'
import { writable } from 'svelte/store'
import { useStore } from '@/lib/store/store'
import useSWRImmutable from 'swr/immutable'
import Placeholder from '@/components/Placeholder/Placeholder'
import { useNormContext } from '@/components/Norm/NormContext'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip/Tooltip'
import { Hovercard, HovercardAnchor, HovercardProvider } from '@ariakit/react'
import Link from 'next/link'

export function HoverOptions ({ children, enumeration, counts, norm }: { children: React.ReactNode, enumeration: string, counts: IClause['counts'], norm: ILaw }) {
  return (
    <HovercardProvider placement='bottom-start' showTimeout={0} hideTimeout={0}>
      <HovercardAnchor render={<Link href={getUrl(norm?.Typ, { norm: norm.abbreviation || norm.normId, abbrNr: norm.abbrNr, enumeration: enumeration })} />}>
        {children}
      </HovercardAnchor>
      <Hovercard unmountOnHide className={styles.hoverMenu}>
        <Row gap={0.25}>
          {/* <InfoButton clauseId={norm.normId} enumeration={enumeration} /> */}
          <CopyButton enumeration={enumeration} />
          {counts?.decision && counts?.decision > 0 && (
            <Button
              variant='flat'
              size='tiny'
              circular
              href={`${getUrl(norm?.Typ, { norm: norm.abbreviation || norm.normId, abbrNr: norm.abbrNr, enumeration })}#rueckverweise`}
              linkProps={{ prefetch: false }}
            >
              <span>{formatNumber(counts.decision ?? 0)} Entscheidung{(counts.decision ?? 0) > 1 ? 'en' : ''}</span>
            </Button>
          )}
          {counts?.clause && counts?.clause > 0 && (
            <Button
              variant='flat'
              size='tiny'
              circular
              href={`${getUrl(norm?.Typ, { norm: norm.abbreviation || norm.normId, abbrNr: norm.abbrNr, enumeration })}#rueckverweise`}
              linkProps={{ prefetch: false }}
            >
              <span>{formatNumber(counts.clause ?? 0)} Paragraf{(counts.clause ?? 0) > 1 ? 'en' : ''}</span>
            </Button>
          )}
          {counts?.rechtssatz && counts?.rechtssatz > 0 && (
            <Button
              variant='flat'
              size='tiny'
              circular
              href={`${getUrl(norm?.Typ, { norm: norm.abbreviation || norm.normId, abbrNr: norm.abbrNr, enumeration })}#rueckverweise`}
              linkProps={{ prefetch: false }}
            >
              <span>{formatNumber(counts.rechtssatz ?? 0)} Rechtss{(counts.rechtssatz ?? 0) > 1 ? 'ätze' : 'atz'}</span>
            </Button>
          )}
        </Row>
      </Hovercard>
    </HovercardProvider>
  )
}

function CopyButton ({ enumeration }: { enumeration: string }) {
  const {
    copied,
    copyText,
  } = useCopyToClipboard()
  function handleCopy () {
    copyText(`${domain}${window.location.pathname}#${urlify(enumeration)}`).catch(console.error)
  }
  return (
    <Tooltip placement='bottom'>
      <TooltipTrigger render={<Button onClick={handleCopy} square variant='flat' size='tiny' circular />}>
        {!copied && <svg><use xlinkHref='/assets/sprite.svg#link' /></svg>}
        {copied && <ClipboardCheck size={16} />}
      </TooltipTrigger>
      <TooltipContent>
        Link kopieren
      </TooltipContent>
    </Tooltip>
  )
}

type OverlayStore = { isOpen: true, clauseId: string, clause?: IClause, enumeration: string } | { isOpen: false }
export const clauseOverlayStore = writable<OverlayStore>({ isOpen: false })

// TODO: Display on Clause details page like we do for laws/decisions
export function ClauseInfoOverlay () {
  const overlayStore = useStore(clauseOverlayStore)
  // @ts-expect-error needs conditional so TS doesn't complain
  const { isOpen, enumeration, clause: _clause } = overlayStore
  const norm = useNormContext()

  // Fetch metadata
  const {
    data,
    isLoading,
    error,
  } = useSWRImmutable((isOpen && enumeration && norm?.normId) ? `/api/laws/${norm.normId}/${enumeration}/metadata` : null)
  const clause = data || _clause

  return (
    <Overlay
      open={isOpen}
      setOpen={(isOpen) => {
        // @ts-expect-error needs conditional so TS doesn't complain
        clauseOverlayStore.set({ isOpen, clauseId: overlayStore.clauseId, enumeration: overlayStore.enumeration })
      }}
    >
      <OverlayContent
        title='Paragrafdetails'
        description={(clause && isOpen) ? `${clause?.enumeration as string} ${clause?.title as string ?? ''}` : ''}
      >
        {isOpen && isLoading && (
          <Placeholder numLines={2} type='text' />
        )}
        {clause && isOpen && !isLoading && !error && (
          <>
            {(clause.Beachte ?? clause.Anmerkung) && (
              <ListBox>
                {clause.Beachte && (
                  <ListBoxRow title='Beachte' subTitle={<span dangerouslySetInnerHTML={{ __html: clause.Beachte }} />} />
                )}
                {clause.Anmerkung && (
                  <ListBoxRow title='Anmerkung' subTitle={<span dangerouslySetInnerHTML={{ __html: clause.Anmerkung }} />} />
                )}
              </ListBox>
            )}

            <ListBox>
              <ListBoxRow title='Zuerst erschienen durch' subTitle={clause.Kundmachungsorgan} />
              <ListBoxRow title='Inkrafttretungsdatum' subTitle={formatDate(clause.Inkrafttretedatum)} />
              <ListBoxRow title='Außerkrafttretungsdatum' subTitle={clause.Ausserkrafttretedatum ? formatDate(clause.Ausserkrafttretedatum) : '—'} />
              {clause.Aenderung && (
                <ListBoxRow title='Geändert durch' subTitle={clause.Aenderung} />
              )}
            </ListBox>

            <ListBox>
              <ListBoxRow title='Paragraf-ID' subTitle={clause.clauseId} />
            </ListBox>

            <div />

            {/* {norm && clause.decisionCount && clause.decisionCount > 0 && (
              <Row gap={0.75}>
                <Button href={`${getUrl(norm?.Typ, { norm: norm.abbreviation || norm.normId, abbrNr: norm.abbrNr, enumeration: clause.enumeration })}#entscheidungen`} variant='accent'>
                  {formatNumber(clause.decisionCount)} Entscheidungen & Rechtssätze
                </Button>
                <Button target='_blank' href={`${getUrl(norm?.Typ, { norm: norm.abbreviation || norm.normId, abbrNr: norm.abbrNr })}/${deterministicUrlify(clause.enumeration)}#entscheidungen`} variant='accent' title='In neuem Tab öffnen' icon={ExternalLink} />
              </Row>
            )} */}
          </>
        )}
      </OverlayContent>
    </Overlay>
  )
}
